import type { MarlinStoreDb } from "../infra/db";
import { getIpJsonContent, saveIpJsonContent } from "../settings/store";
import type { IpJsonContent, SingleSite } from "../types";

type IpJsonSite = NonNullable<IpJsonContent["SITES"]>[number];

export const IP_JSON_PORT = "3050";

const isValidIdentifier = (value: unknown): boolean =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

export function assertValidIpJsonSites(
  sites: unknown,
): asserts sites is IpJsonSite[] {
  if (!Array.isArray(sites))
    throw new Error("No sites found in the selected file");
  sites.forEach((value: unknown, index) => {
    if (
      typeof value !== "object" ||
      value === null ||
      Array.isArray(value) ||
      !("IP" in value) ||
      typeof value.IP !== "string" ||
      !value.IP.trim() ||
      !("PORT" in value) ||
      typeof value.PORT !== "string" ||
      !value.PORT.trim() ||
      ("NAME" in value &&
        value.NAME !== undefined &&
        typeof value.NAME !== "string") ||
      ("CLIENTID" in value &&
        value.CLIENTID !== undefined &&
        !isValidIdentifier(value.CLIENTID)) ||
      ("machine_id" in value &&
        value.machine_id !== undefined &&
        !isValidIdentifier(value.machine_id))
    )
      throw new Error(`Invalid site at index ${index + 1}`);
  });
}

const nextClientId = (ipJson: IpJsonContent): number =>
  (ipJson.SITES ?? []).reduce(
    (highest, site) => Math.max(highest, site.CLIENTID ?? 0),
    0,
  ) + 1;

const ensureShape = (ipJson: IpJsonContent | null): IpJsonContent => ({
  SCREENS: [],
  SELECTED_SITE: 0,
  SITES: [],
  ...(ipJson ?? {}),
});

export const mirrorSiteIntoIpJson = (
  db: MarlinStoreDb,
  site: SingleSite,
  previous: SingleSite | undefined,
): Promise<IpJsonContent> =>
  db.transaction("rw", db.settings, async () => {
    const ipJson = ensureShape(await getIpJsonContent(db));
    const sites = ipJson.SITES!;
    const siteName = site.name.includes(" - ")
      ? site.name.split(" - ")[0]
      : site.name;
    const add = () =>
      sites.push({
        IP: site.ip,
        PORT: IP_JSON_PORT,
        NAME: siteName,
        CLIENTID: nextClientId(ipJson),
      });

    if (previous && previous.ip !== site.ip) {
      let updated = 0;
      for (const entry of sites)
        if (entry.IP === previous.ip) {
          entry.IP = site.ip;
          entry.PORT = IP_JSON_PORT;
          updated += 1;
        }
      if (updated === 0) add();
    } else {
      const existing = sites.find((entry) => entry.IP === site.ip);
      if (existing) {
        existing.PORT = IP_JSON_PORT;
        existing.NAME = siteName;
      } else add();
    }
    await saveIpJsonContent(db, ipJson);
    return ipJson;
  });

export const removeSiteFromIpJson = (
  db: MarlinStoreDb,
  site: SingleSite,
): Promise<IpJsonContent | null> =>
  db.transaction("rw", [db.settings, db.machines], async () => {
    const ipJson = await getIpJsonContent(db);
    if (!ipJson?.SITES) return null;
    const stillUsed = (await db.machines.toArray()).some(
      (m) => m.ip === site.ip && m._id !== site._id,
    );
    if (stillUsed) return null;
    const remaining = ipJson.SITES.filter((entry) => entry.IP !== site.ip);
    if (remaining.length === ipJson.SITES.length) return null;
    const next = { ...ipJson, SITES: remaining };
    await saveIpJsonContent(db, next);
    return next;
  });

export const addMachinesFromIpJsonSites = (
  db: MarlinStoreDb,
  sites: IpJsonSite[],
): Promise<number> =>
  db.transaction("rw", db.machines, async () => {
    const machines = await db.machines.toArray();
    const seen = new Set(machines.map((m) => m.ip));
    let nextId =
      machines.reduce((highest, m) => Math.max(highest, m._id), 0) + 1;
    let added = 0;
    for (const site of sites) {
      if (!site.IP || !site.PORT || seen.has(site.IP)) continue;
      seen.add(site.IP);
      await db.machines.put({
        _id: nextId,
        machine_id: typeof site.machine_id === "number" ? site.machine_id : 0,
        site_id: site.CLIENTID ?? 0,
        name: site.NAME
          ? `${site.NAME}${site.CLIENTID ? ` - ${site.CLIENTID}` : ""}`
          : `Site${site.CLIENTID ? ` - ${site.CLIENTID}` : " (unnamed)"}`,
        ip: site.IP,
        port: site.PORT,
        cages: [],
      });
      nextId += 1;
      added += 1;
    }
    return added;
  });

export const replaceSitesFromIpJson = (
  db: MarlinStoreDb,
  sites: IpJsonSite[],
): Promise<number> =>
  db.transaction("rw", db.machines, async () => {
    assertValidIpJsonSites(sites);
    await db.machines.clear();
    return addMachinesFromIpJsonSites(db, sites);
  });
